import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './styles/Landlord.css';

const Settings = () => {
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [validations, setValidations] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
    passwordsMatch: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const base_url = 'https://api.homehive.co.zw/api';

  // Validate password as user types
  useEffect(() => {
    const newPassword = passwords.new_password;
    setValidations({
      passwordsMatch: newPassword === passwords.confirm_password && newPassword !== ''
    });
  }, [passwords.new_password, passwords.confirm_password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isPasswordValid = () => {
    return Object.values(validations).every(value => value === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please ensure your password meets all requirements',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${base_url}/user/change-password`,
        {
          current_password: passwords.current_password,
          new_password: passwords.new_password
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      Swal.fire({
        title: 'Success',
        text: 'Password updated successfully',
        icon: 'success',
        confirmButtonColor: '#1a237e'
      });

      // Clear form
      setPasswords({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update password',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="settings-container">

      <div className="settings-card">
        <div style={{marginBottom: '20px'}} className="card-header">
          <h2>
            <i className="fas fa-lock"></i>
            Change Password
          </h2>
          
        </div>

        <form onSubmit={handleSubmit} className="settings-form">
          <div className="settings-layout">
            {/* Left Column - Password Fields */}
            <div className="password-fields-column">
              <div className="form-group mb-3">
                <label className='pb-2'>
                  <i className="fas fa-key"></i>
                  &nbsp;&nbsp;
                  Current Password
                </label>
                <div className="password-input">
                  <input
                    type="password"
                    name="current_password"
                    value={passwords.current_password}
                    onChange={handleChange}
                    required
                    className="form-control"
                    placeholder="Enter your current password"
                  />
                </div>
              </div>

              <div className="form-group mb-3">
                <label className='pb-2'>
                  <i className="fas fa-lock"></i>
                  &nbsp;&nbsp;
                  New Password
                </label>
                <div className="password-input">
                  <input
                    type="password"
                    name="new_password"
                    value={passwords.new_password}
                    onChange={handleChange}
                    required
                    className="form-control"
                    placeholder="Enter new password"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className='pb-2'>
                  <i className="fas fa-lock"></i>
                  &nbsp;&nbsp;
                  Confirm New Password
                </label>
                <div className="password-input">
                  <input
                    type="password"
                    name="confirm_password"
                    value={passwords.confirm_password}
                    onChange={handleChange}
                    required
                    className="form-control"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Password Requirements */}
            <div className="password-requirements-column">
              <div className="password-requirements">
                <h3>Password Requirements:</h3>
                <ul>
                  <li className={validations.passwordsMatch ? 'valid' : ''}>
                    <i className={`fas fa-${validations.passwordsMatch ? 'check' : 'times'}-circle`}></i>
                    Passwords match
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isLoading || !isPasswordValid()}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Updating Password...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Update Password
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add CSS styles for the side-by-side layout
const style = document.createElement('style');
style.innerHTML = `
.settings-layout {
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
}

.password-fields-column {
  flex: 1;
  min-width: 0;
}

.password-requirements-column {
  flex: 1;
  min-width: 0;
}

.password-requirements {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  height: fit-content;
}

.password-requirements h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
  transition: color 0.3s ease;
}

.password-requirements li:last-child {
  margin-bottom: 0;
}

.password-requirements li i {
  margin-right: 8px;
  font-size: 12px;
}

.password-requirements li.valid {
  color: #28a745;
}

.password-requirements li.valid i {
  color: #28a745;
}

.password-requirements li:not(.valid) i {
  color: #dc3545;
}

/* Responsive design */
@media screen and (max-width: 768px) {
  .settings-layout {
    flex-direction: column;
    gap: 20px;
  }
  
  .password-requirements {
    order: -1;
  }
}

@media screen and (max-width: 480px) {
  .settings-layout {
    gap: 15px;
  }
  
  .password-requirements {
    padding: 15px;
  }
  
  .password-requirements h3 {
    font-size: 14px;
  }
  
  .password-requirements li {
    font-size: 13px;
  }
}
`;
document.head.appendChild(style);

export default Settings; 